import cn from 'classnames';
import {
  IDictationItem,
  IDictationTask,
  IDictationTaskItemSession,
  IDictationTaskSession,
  tDictationAnswer
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { useCallback, useEffect } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import { customMediaQuery } from 'students/views/shared/styled';
import styled from 'styled-components';

import { SPlayTaskWrapper } from '../../common/components';
import Streak from '../../common/components/Streak';
import { usePlayAnswers, useStreakFactor } from '../../common/hooks';
import PlayTaskScreenSwitcher from '../../common/screens/PlayTaskScreenSwitcher';
import { IPlayTaskProps, tPlayTaskComponentType } from '../types';
import PlayDictationTaskMainScreen from './PlayDictationTaskMainScreen';
import bgImage from './assets/dictationTaskBg.svg';
import { ISelectAnswerPayload } from './components/DictationTaskItem';

const PlayDictationTask: tPlayTaskComponentType<
  IPlayTaskProps<IDictationTask, IDictationTaskSession>
> = ({ task, taskSession, isCompleting, onFinish, onNext, onAnswer, className }) => {
  const { items, instruction } = task;
  const answersFn = usePlayAnswers<tDictationAnswer>();

  const { streakNumber, streakPercent, onTheFirstTry } = useStreakFactor();
  const { makeDirty } = usePromptContext();

  useEffect(() => {
    if (taskSession?.taskItemSessions) {
      answersFn(taskSession.taskItemSessions);
    } else {
      answersFn(
        items.map((item: IDictationItem, index: number) => {
          return {
            id: index,
            completed: false,
            answer: '',
            taskItemId: item.id,
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }

    onTheFirstTry(false);
  }, [taskSession, items, onTheFirstTry, answersFn]);

  const updateAnswer = (itemId: number, data: Partial<tDictationAnswer[number]>) => {
    const answerIndex = answersFn().findIndex(
      (answer: IDictationTaskItemSession) => answer.taskItemId === itemId
    );

    const { mistakesCount, skipped } = data;

    if (answerIndex < 0) return;

    const answer = answersFn()[answerIndex];
    const newAnswerData = { ...answer, ...data };

    answersFn([
      ...answersFn().slice(0, answerIndex),
      newAnswerData,
      ...answersFn().slice(answerIndex + 1)
    ]);

    onAnswer && onAnswer(newAnswerData);

    onTheFirstTry(!(mistakesCount || skipped) && answer.completed);
  };

  const handleSelectAnswer = (payload: ISelectAnswerPayload) => {
    const { itemId, mistakesCount, answer } = payload;

    updateAnswer(itemId, {
      mistakesCount,
      answer,
      skipped: false
    });

    makeDirty(true);
  };

  const handleSkip = (itemId: number) => {
    updateAnswer(itemId, {
      skipped: true
    });
  };

  const handleFinishTask = () => {
    onNext && onNext();
  };

  const submitTask = useCallback(() => {
    onFinish && onFinish(answersFn());
  }, [onFinish, answersFn]);

  const handleCompleteTask = (goToFinishScreen: () => void) => () => {
    submitTask();

    goToFinishScreen();
  };

  return (
    <SCustomPlayTaskWrapper className={cn(className)}>
      <Streak streakNumber={streakNumber} streakPercent={streakPercent} />
      <SPlayTaskScreenSwitcher
        isCompleting={isCompleting}
        onExitFinishScreen={handleFinishTask}
        className={className}
        startScreenHeading={
          instruction ||
          t('frontend.lesson_task.tasks.play.arrange_words.start_screen.heading')
        }
        itemsExist={!!items.length}
        taskStatus={taskSession?.status}
        completeTask={submitTask}
        taskColor="#934cbe"
      >
        {(goToFinishScreen) => (
          <PlayDictationTaskMainScreen
            task={task}
            taskSession={taskSession}
            onSelectAnswer={handleSelectAnswer}
            onComplete={handleCompleteTask(goToFinishScreen)}
            onSkip={handleSkip}
          />
        )}
      </SPlayTaskScreenSwitcher>
    </SCustomPlayTaskWrapper>
  );
};

export default PlayDictationTask;

PlayDictationTask.backgroundComponent = styled.div`
  background-color: #adadad;
  background-image: linear-gradient(180deg, #bc739b 0.04%, #934cbe 100%);

  ${customMediaQuery('tablet')} {
    background-image: url(${bgImage}),
      linear-gradient(180deg, #bc739b 0.04%, #934cbe 100%);
    background-repeat: no-repeat, no-repeat;
    background-position: center;
    background-size: cover, auto;
  }
`;
PlayDictationTask.bgColor = '#adadad';

const SCustomPlayTaskWrapper = styled(SPlayTaskWrapper)`
  justify-content: center;
`;

const SPlayTaskScreenSwitcher = styled(PlayTaskScreenSwitcher)`
  width: 100%;
`;
